import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { signUpQueryParams } from '@minecraft.utils';
import { QUERY_PARAMS, useUserContext } from '@blocs.auth';
import { usePermissions } from '@minecraft.permissions';
import { useBillingPopup } from './BillingPopupContext';
import { PAYWALL_QUERY_PARAMS, PAYWALL_STEPS } from './enums/paywall';
import { PaywallLocation } from './enums/PaywallLocation';

const PAYWALL_UPGRADE = 'UPGRADE';
const UPGRADE_PARAMS_KEY = 'paywall-upgrade';
const PAYWALL_LOCATION = 'paywall-location';

export const usePaywallQueryParams = ({
  step,
  paywallLocation,
}: {
  step: PAYWALL_STEPS;
  paywallLocation?: PaywallLocation;
}) => {
  const history = useHistory();
  const location = useLocation();
  const billingPopup = useBillingPopup();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { ability } = usePermissions();
  const { subscription } = useUserContext();

  useEffect(() => {
    const isDeeplink = localStorage.getItem(QUERY_PARAMS.deeplink as string)?.toLocaleLowerCase() === 'true';
    const canUpgrade = ability.can('upgrade', 'subscription');
    if (isDeeplink && !!subscription && canUpgrade) {
      billingPopup.show({
        location: PaywallLocation.DEEPLINK,
      });
    }
    localStorage.removeItem(QUERY_PARAMS.deeplink as string);

    if (paywallLocation !== PaywallLocation.MEMBERSHIP) {
      const queryParamsMap = {
        [PaywallLocation.SIGNUP]: {
          [PAYWALL_STEPS.INITIAL]: signUpQueryParams.queryParamsSelectMembership,
          [PAYWALL_STEPS.CHECKOUT]: signUpQueryParams.queryParamsMembershipSummary,
          [PAYWALL_STEPS.SUCCESS]: signUpQueryParams.queryParamsMembershipSuccess,
        },
        [PAYWALL_UPGRADE]: {
          [PAYWALL_STEPS.INITIAL]: new URLSearchParams({
            [UPGRADE_PARAMS_KEY]: PAYWALL_QUERY_PARAMS.SELECT_MEMBERSHIP,
          }),
          [PAYWALL_STEPS.CHECKOUT]: new URLSearchParams({
            [UPGRADE_PARAMS_KEY]: PAYWALL_QUERY_PARAMS.MEMBERSHIP_SUMMARY,
          }),
          [PAYWALL_STEPS.SUCCESS]: new URLSearchParams({
            [UPGRADE_PARAMS_KEY]: PAYWALL_QUERY_PARAMS.MEMBERSHIP_SUCCESS,
          }),
        },
      };

      const newParams: URLSearchParams =
        queryParamsMap[paywallLocation === PaywallLocation.SIGNUP ? PaywallLocation.SIGNUP : PAYWALL_UPGRADE][
          step ?? PAYWALL_STEPS.INITIAL
        ];

      newParams.forEach((value, key) => {
        if (!value || !paywallLocation) {
          searchParams.delete(key);
        } else {
          searchParams.set(key, value);
        }
      });

      if (!paywallLocation) {
        searchParams.delete(PAYWALL_LOCATION);
      } else {
        searchParams.set(PAYWALL_LOCATION, paywallLocation);
      }
    }

    history.push({ search: searchParams.toString() });
  }, [history, location.search, paywallLocation, step, searchParams, billingPopup, subscription]);
};
